import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useHubFailure, useShownHub } from 'hooks/useShownHub'
import { useAuth } from 'hooks/auth'
import { IFolderContents, IProject, ISite } from 'types'
import Toggle from 'components/toggle'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import { HOME } from 'routes'
import DuplicateChildren from 'components/duplicateChildren'
import DuplicateHubDialog from 'views/settings/components/information/duplicateHubDialog'
import HubActionDialog from 'views/settings/components/information/hubActionDialog'
import Select from 'components/select'
import Spinner from 'components/spinner'
import Input from 'components/input'
import { BLUE_BUTTON, SaveButtons } from 'components/save-buttons'
import {
  SETTINGS_CANVAS,
  ConfirmDialog,
  Confirmation,
  FieldRow,
  InlineRow,
  SettingsCard,
  TextLink,
  WhyDisabled,
  useFocusWithin
} from 'views/settings/components/settingsCard'

// Why the hub's site can't be changed here: an Owner from another organisation can't take the hub
// out of its site.
const siteLockedNote = (project: IProject): string | undefined => {
  if (project.canChangeSite !== false) return undefined
  const organisation = project.site?.organisation?.name || "the site's organisation"
  return `Only people from ${organisation} can move this ${PROJECT} to another site.`
}

const InformationBox: React.FC = () => {
  const { project, updateProject, projectOwner } = useProject()
  const stillShown = useShownHub(project.publicId)
  const hubFailed = useHubFailure(project.publicId)
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const navigate = useNavigate()
  const [archiveProjectDialog, setArchiveProjectDialog] = useState<boolean>(false)
  const [restoreProjectDialog, setRestoreProjectDialog] = useState<boolean>(false)
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState<boolean>(false)
  const [savingInfo, setSavingInfo] = useState<boolean>(false)
  const [editName, setEditName] = useState<{ editing: boolean; name: string }>({
    editing: false,
    name: project.name
  })
  const [editDomains, setEditDomains] = useState<{ editing: boolean; text: string }>({
    editing: false,
    text: project.domainsAccess.join(', ')
  })

  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const { user } = useAuth()
  const nameInput = useRef<HTMLInputElement>(null)
  const domainsInput = useRef<HTMLInputElement>(null)
  const [nameFocused, nameFocusProps, leaveName] = useFocusWithin()
  const [domainsFocused, domainsFocusProps, leaveDomains] = useFocusWithin()

  // Follow the saved values while they aren't being edited, so the fields show a change made elsewhere.
  useEffect(() => {
    if (!editName.editing) setEditName({ editing: false, name: project.name })
  }, [project.name])
  useEffect(() => {
    if (!editDomains.editing) setEditDomains({ editing: false, text: project.domainsAccess.join(', ') })
  }, [project.domainsAccess])
  // Another hub's settings: drop anything typed, or a confirmation left open, for the last one,
  // rather than carry it over to save here.
  useEffect(() => {
    setEditName({ editing: false, name: project.name })
    setEditDomains({ editing: false, text: project.domainsAccess.join(', ') })
    setConfirmation(null)
  }, [project.publicId])
  const [processes, setProcesses] = useState<IFolderContents[]>([])
  const [loadingProcesses, setLoadingProcesses] = useState<boolean>(false)
  const [duplicateModal, setDuplicateModal] = useState<boolean>(false)
  const [deletingProject, setDeletingProject] = useState<boolean>(false)
  const [viewDuplicatesModal, setViewDuplicatesModal] = useState<boolean>(false)
  // Another hub's settings: close any dialog opened for the last one, so it can't act on this one.
  useEffect(() => {
    setDuplicateModal(false)
    setViewDuplicatesModal(false)
    setArchiveProjectDialog(false)
    setRestoreProjectDialog(false)
    setPermanentDeleteDialog(false)
  }, [project.publicId])

  useEffect(() => {
    if (project.publicId === '') return
    // A hub's documents arriving after a switch to another hub are dropped, success or failure.
    let cancelled = false
    setLoadingProcesses(true)
    api
      .getProjectResources(project.publicId, { projectPermissions: true, excludeTables: true })
      .then((response) => {
        if (!cancelled) setProcesses(response.data)
      })
      .catch((error) => {
        if (!cancelled) displayErrorMessage(error)
      })
      .then(() => {
        if (!cancelled) setLoadingProcesses(false)
      })
    return () => {
      cancelled = true
    }
  }, [project?.publicId])

  const [sites, setSites] = useState<ISite[]>([])
  const [loadingSites, setLoadingSites] = useState<boolean>(false)

  useEffect(() => {
    const fetchSites = async () => {
      let response
      setLoadingSites(true)
      try {
        response = await api.getUserSites()
      } catch (error) {
        displayErrorMessage(error)
        setLoadingSites(false)
        return
      }
      setLoadingSites(false)
      let fetchedSites: ISite[] = response.data

      // Ensure the currently assigned site is present, even if user has no access
      if (project.site && project.site.publicId) {
        const hasCurrentSite = fetchedSites.some((site) => site.publicId === project.site!.publicId)

        if (!hasCurrentSite) {
          fetchedSites = [
            ...fetchedSites,
            {
              ...project.site,
              // Fallbacks in case any fields are missing
              name: project.site.name || 'Current Hub Site',
              organisation: project.site.organisation || {
                name: 'No Organisation',
                publicId: ''
              }
            } as ISite
          ]
        }
      }

      setSites(fetchedSites)
    }

    if (project && project.publicId !== '' && projectOwner) {
      fetchSites()
    }
  }, [project, projectOwner, displayErrorMessage])

  const saveProjectInfo = (
    field:
      | 'name'
      | 'defaultProcessId'
      | 'allowTableExport'
      | 'allowDocumentExport'
      | 'public'
      | 'domainsAccess'
      | 'mfaRequired'
      | 'hideProcessCreated'
      | 'site',
    value: string | boolean | string[]
  ) => {
    setSavingInfo(true)
    const hub = project.publicId
    const hubName = project.name
    const newProjectInfo = { ...project }
    if (field === 'name' && typeof value === 'string') newProjectInfo['name'] = value
    else if (field === 'defaultProcessId' && typeof value === 'string') newProjectInfo['defaultProcessId'] = value
    else if (field === 'allowTableExport' && typeof value === 'boolean') newProjectInfo['allowTableExport'] = value
    else if (field === 'allowDocumentExport' && typeof value === 'boolean')
      newProjectInfo['allowDocumentExport'] = value
    else if (field === 'public' && typeof value === 'boolean') newProjectInfo['public'] = value
    else if (field === 'domainsAccess' && Array.isArray(value)) newProjectInfo['domainsAccess'] = value
    else if (field === 'mfaRequired' && typeof value === 'boolean') newProjectInfo['mfaRequired'] = value
    else if (field === 'hideProcessCreated' && typeof value === 'boolean') newProjectInfo['hideProcessCreated'] = value
    else if (field === 'site' && typeof value === 'string')
      newProjectInfo['site'] = {
        publicId: value,
        name: '',
        users: []
      }

    // Only send the changed field to avoid permission errors on unrelated fields
    const updatePayload: Parameters<typeof api.updateProject>[0] = { publicId: newProjectInfo.publicId }
    if (field === 'site') {
      if (!newProjectInfo.site?.publicId) {
        // A hub cannot be detached from its site, so never send an empty site.
        setSavingInfo(false)
        return
      }
      updatePayload.site = { publicId: newProjectInfo.site.publicId }
    } else if (field === 'name') {
      updatePayload.name = newProjectInfo.name
    } else if (field === 'defaultProcessId') {
      updatePayload.defaultProcessId = newProjectInfo.defaultProcessId
    } else if (field === 'allowTableExport') {
      updatePayload.allowTableExport = newProjectInfo.allowTableExport
    } else if (field === 'allowDocumentExport') {
      updatePayload.allowDocumentExport = newProjectInfo.allowDocumentExport
    } else if (field === 'public') {
      updatePayload.public = newProjectInfo.public
    } else if (field === 'domainsAccess') {
      updatePayload.domainsAccess = newProjectInfo.domainsAccess
    } else if (field === 'mfaRequired') {
      updatePayload.mfaRequired = newProjectInfo.mfaRequired
    } else if (field === 'hideProcessCreated') {
      updatePayload.hideProcessCreated = newProjectInfo.hideProcessCreated
    }

    api
      .updateProject(updatePayload)
      .then(() => {
        // Saved to the hub it was made in: once another hub is shown, that hub and its page are left alone.
        updateProject((current) => (current.publicId === hub ? newProjectInfo : current))
        setSavingInfo(false)
        if (!stillShown(hub)) return
        // Only the field just saved stops editing: a switch flipped meanwhile keeps a name or
        // domains still being typed.
        if (field === 'name') setEditName({ editing: false, name: newProjectInfo.name })
        if (field === 'domainsAccess') setEditDomains({ editing: false, text: newProjectInfo.domainsAccess.join(', ') })
        setSnackbarMessage({
          status: 'success'
        })
      })
      .catch((error) => {
        hubFailed(error, hub, `${hubName}'s settings couldn't be saved. Open its settings to try again.`)
        setSavingInfo(false)
      })
  }

  // Each action names the hub it started in, so one finishing after a hub switch updates that hub (if it's
  // still the one on show) and never the hub shown since.
  const deleteProject = async (): Promise<void> => {
    const hub = project.publicId
    const hubName = project.name
    await api
      .deleteProject(hub)
      .then(() => {
        updateProject((current) =>
          current.publicId === hub ? { ...current, isDeleted: true, deletedAt: Date.now().toString() } : current
        )
      })
      .catch((error) => {
        hubFailed(error, hub, `${hubName} couldn't be deactivated. Open its settings to try again.`)
        return Promise.reject(error)
      })
  }

  const restoreProject = async (): Promise<void> => {
    const hub = project.publicId
    const hubName = project.name
    await api
      .restoreProject({ projectId: hub })
      .then(() => {
        updateProject((current) =>
          current.publicId === hub ? { ...current, isDeleted: false, deletedAt: null } : current
        )
        // Once another hub is shown, the message names the hub reactivated, so it isn't read as this one.
        if (stillShown(hub)) setSnackbarMessage({ status: 'success' })
        else setSnackbarMessage({ status: 'success', message: `${hubName} was reactivated` })
      })
      .catch((error) => {
        hubFailed(error, hub, `${hubName} couldn't be reactivated. Open its settings to try again.`)
        return Promise.reject(error)
      })
  }

  const permanentDeleteProject = async (): Promise<void> => {
    const hub = project.publicId
    const hubName = project.name
    setDeletingProject(true)
    try {
      await api.permanentlyDeleteProject(hub)
      // Away from the deleted hub, if it's still on show; otherwise just say it's done.
      if (stillShown(hub)) navigate(HOME)
      else setSnackbarMessage({ status: 'success', message: `${hubName} was permanently deleted` })
    } catch (error) {
      hubFailed(error, hub, `${hubName} couldn't be permanently deleted. Open its settings to try again.`)
      setDeletingProject(false)
      return Promise.reject(error)
    }
    setDeletingProject(false)
  }

  const handleOnPublicHubChange = () => {
    if (project.mfaRequired) {
      setSnackbarMessage({
        status: 'error',
        message: `A ${PROJECT} that requires multi-factor authentication (MFA) can't be made public. Turn off the MFA requirement first.`
      })
      return
    }
    saveProjectInfo('public', true)
  }

  // Making the hub public asks first; making it private doesn't. (With MFA required the switch is
  // disabled, and says why on hover.)
  const togglePublic = (toggled: boolean) => {
    if (!toggled) {
      saveProjectInfo('public', false)
      return
    }
    setConfirmation({
      title: `Make this ${PROJECT} public?`,
      message: `Anyone with the link will be able to open this ${PROJECT} and see its documents and tables, without signing in.`,
      confirmLabel: `Make ${capitaliseFirstLetter(PROJECT)} Public`,
      onConfirm: () => handleOnPublicHubChange()
    })
  }

  if (deletingProject) {
    return (
      <div className="w-full bg-surface-sunken z-[2] h-[calc(100%_-_44px)]">
        <div className="truncate rounded m-[5px] p-[5px] min-w-[25px] font-semibold flex items-center border border-transparent justify-around cursor-pointer transition-[background-color] duration-[85ms] ease-in">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full min-h-full overflow-auto ${SETTINGS_CANVAS}`}>
      <div className="flex flex-col gap-4 max-w-[720px] mx-auto">
        <SettingsCard title="Hub details">
          <FieldRow
            label={`${capitaliseFirstLetter(PROJECT)} name`}
            note={`${capitaliseFirstLetter(PROJECT)} names are shown in the sidebar, search and All Hubs.`}
          >
            <div className="flex items-center gap-2" {...nameFocusProps}>
              <Input
                ref={nameInput}
                value={editName.name}
                disabled={!projectOwner}
                data-tour="hub-name-input"
                aria-label={`${capitaliseFirstLetter(PROJECT)} name`}
                onChange={(event) => setEditName({ editing: true, name: event.target.value })}
              />
              {(nameFocused || editName.editing) && projectOwner && (
                <SaveButtons
                  saving={savingInfo}
                  canSave={editName.name.trim() !== '' && editName.name !== project.name}
                  onCancel={() => {
                    setEditName({ editing: false, name: project.name })
                    nameInput.current?.blur()
                    leaveName()
                  }}
                  onSave={() => saveProjectInfo('name', editName.name)}
                />
              )}
            </div>
          </FieldRow>

          {projectOwner && (
            <FieldRow
              label={`${capitaliseFirstLetter(PROJECT)} home document`}
              description={`The first page members see when they open this ${PROJECT}.`}
              note={`Only documents every member can open can be the home document.`}
              tour="default-document-selector"
            >
              <Select
                options={[
                  { value: '', label: 'None Selected' },
                  ...processes.map((folder: IFolderContents) => ({
                    value: folder.process?.publicId || '',
                    label: folder.process?.name || ''
                  }))
                ]}
                loading={loadingProcesses}
                optionsSelected={project.defaultProcessId ? [project.defaultProcessId] : ['']}
                onOptionClick={(option) => saveProjectInfo('defaultProcessId', option)}
              />
            </FieldRow>
          )}

          {projectOwner && (loadingSites || sites.length > 0) && (
            <FieldRow label="Organisation site" note={siteLockedNote(project)}>
              <Select
                disabled={project.canChangeSite === false}
                options={sites
                  .filter((site: ISite) => site.publicId)
                  .map((site: ISite) => ({
                    label: site.name,
                    value: site.publicId!,
                    group: site.organisation ? site.organisation.name : 'No Organisation'
                  }))}
                loading={loadingSites}
                optionsSelected={project.site && project.site.publicId ? [project.site.publicId] : []}
                placeholder="Select a site"
                onOptionClick={(option: string) => {
                  if (!option || (project.site && project.site.publicId === option)) {
                    return
                  }

                  saveProjectInfo('site', option)
                }}
                groupBy={true}
              />
            </FieldRow>
          )}
        </SettingsCard>

        {projectOwner && (
          <SettingsCard title="Access">
            <InlineRow
              label={`Public ${PROJECT}`}
              description={`Anyone with the link can open this ${PROJECT}.`}
              tour={project.public ? 'make-hub-private' : 'make-hub-public'}
            >
              <WhyDisabled
                reason={
                  project.mfaRequired ? 'Not available while multi-factor authentication is required.' : undefined
                }
              >
                <Toggle
                  aria-label={`Public ${PROJECT}`}
                  toggled={project.public}
                  disabled={savingInfo || project.mfaRequired}
                  onToggle={togglePublic}
                />
              </WhyDisabled>
            </InlineRow>

            <FieldRow
              label="Domain member access"
              description={`Allow anyone signed in with an email from these domains to access this ${PROJECT} without an invitation.`}
              note="Separate domains with commas, for example @yourcompany.com, @yoursubsidiary.com."
            >
              <div className="flex items-center gap-2" {...domainsFocusProps}>
                <Input
                  ref={domainsInput}
                  value={editDomains.text}
                  placeholder="@yourcompany.com, @yoursubsidiary.com"
                  aria-label="Domain member access"
                  onChange={(event) => setEditDomains({ editing: true, text: event.target.value })}
                />
                {(domainsFocused || editDomains.editing) && (
                  <SaveButtons
                    saving={savingInfo}
                    canSave={editDomains.text !== project.domainsAccess.join(', ')}
                    onCancel={() => {
                      setEditDomains({ editing: false, text: project.domainsAccess.join(', ') })
                      domainsInput.current?.blur()
                      leaveDomains()
                    }}
                    onSave={() => saveProjectInfo('domainsAccess', editDomains.text ? editDomains.text.split(',') : [])}
                  />
                )}
              </div>
            </FieldRow>

            <InlineRow
              label={`Require multi-factor authentication (MFA) for this ${PROJECT}`}
              description={`Force members to have MFA on their account to access this ${PROJECT}.`}
            >
              <WhyDisabled reason={project.public ? `Not available on public ${PROJECT}s.` : undefined}>
                <Toggle
                  aria-label={`Require multi-factor authentication (MFA) for this ${PROJECT}`}
                  toggled={project.mfaRequired}
                  disabled={project.public || savingInfo}
                  onToggle={(toggled) =>
                    setConfirmation(
                      toggled
                        ? {
                            title: 'Require multi-factor authentication?',
                            message: `Members who haven't set up multi-factor authentication will need to before they can open this ${PROJECT}.`,
                            // Requiring MFA shuts out everyone without it, including the person turning it
                            // on, who then can't reach this setting to turn it off.
                            warning: user?.is2FaEnabled
                              ? undefined
                              : `You haven't set up two-factor authentication. Unless you sign in with Google or Microsoft, you'll lose access to this ${PROJECT}, and to this setting, until you do.`,
                            confirmLabel: 'Require MFA',
                            onConfirm: () => saveProjectInfo('mfaRequired', true)
                          }
                        : {
                            title: 'Stop requiring multi-factor authentication?',
                            message: `Members will be able to open this ${PROJECT} with just their password.`,
                            confirmLabel: 'Stop Requiring MFA',
                            onConfirm: () => saveProjectInfo('mfaRequired', false)
                          }
                    )
                  }
                />
              </WhyDisabled>
            </InlineRow>
          </SettingsCard>
        )}

        {projectOwner && (
          <SettingsCard title="Documents and exports">
            <InlineRow label="Hide who created each document">
              <Toggle
                aria-label="Hide who created each document"
                toggled={project.hideProcessCreated}
                disabled={savingInfo}
                onToggle={(toggled) => saveProjectInfo('hideProcessCreated', toggled)}
              />
            </InlineRow>
            <InlineRow label="Members can export documents">
              <Toggle
                aria-label="Members can export documents"
                toggled={project.allowDocumentExport}
                disabled={savingInfo}
                onToggle={(toggled) => saveProjectInfo('allowDocumentExport', toggled)}
              />
            </InlineRow>
            <InlineRow label="Members can export tables">
              <Toggle
                aria-label="Members can export tables"
                toggled={project.allowTableExport}
                disabled={savingInfo}
                onToggle={(toggled) => saveProjectInfo('allowTableExport', toggled)}
              />
            </InlineRow>
          </SettingsCard>
        )}

        {projectOwner && (
          <SettingsCard title="Duplicates">
            <InlineRow
              label={`Duplicate this ${PROJECT}`}
              // The copies already made, as a link under the label.
              note={<TextLink onClick={() => setViewDuplicatesModal(true)}>View existing duplicates</TextLink>}
            >
              <Button internalType="outline" onClick={() => setDuplicateModal(true)}>
                Duplicate {capitaliseFirstLetter(PROJECT)}
              </Button>
            </InlineRow>
          </SettingsCard>
        )}

        {projectOwner && (
          <SettingsCard title="Danger zone" description={`Stop or remove this ${PROJECT}. Take care.`} danger>
            <InlineRow
              label={project.isDeleted ? `Reactivate ${PROJECT}` : `Deactivate ${PROJECT}`}
              description={
                project.isDeleted
                  ? `This ${PROJECT} is deactivated: documents and tables cannot be viewed or edited, and syncs are paused.`
                  : `Documents and tables cannot be viewed or edited, and syncs are paused, until the ${PROJECT} is reactivated.`
              }
            >
              {project.isDeleted ? (
                <Button
                  internalType="outline"
                  className={BLUE_BUTTON}
                  onClick={() => setRestoreProjectDialog(true)}
                  disabled={savingInfo}
                >
                  Reactivate {capitaliseFirstLetter(PROJECT)}
                </Button>
              ) : (
                <Button
                  internalType="danger"
                  onClick={() => setArchiveProjectDialog(true)}
                  data-cy="archive-project-button"
                  disabled={savingInfo}
                >
                  Deactivate {capitaliseFirstLetter(PROJECT)}
                </Button>
              )}
            </InlineRow>

            <InlineRow
              label={`Permanently delete ${PROJECT}`}
              description={`Deletes everything in this ${PROJECT}, and can't be undone. Back up anything you need first.${project.isDeleted ? '' : ` Deactivate the ${PROJECT} before deleting it.`}`}
            >
              <Button
                internalType="danger"
                onClick={() => setPermanentDeleteDialog(true)}
                data-cy="archive-project-button"
                disabled={savingInfo || !project.isDeleted}
                title={
                  !project.isDeleted ? `You must deactivate the ${PROJECT} before you can permanently delete it` : ''
                }
              >
                Permanently Delete {capitaliseFirstLetter(PROJECT)}
              </Button>
            </InlineRow>
          </SettingsCard>
        )}
      </div>

      <ConfirmDialog confirmation={confirmation} onClose={() => setConfirmation(null)} />

      {duplicateModal && <DuplicateHubDialog onClose={() => setDuplicateModal(false)} />}
      {viewDuplicatesModal && (
        <DuplicateChildren
          id="view-duplicates"
          open={viewDuplicatesModal}
          setOpen={setViewDuplicatesModal}
          type="project"
          publicId={project.publicId}
        />
      )}

      {archiveProjectDialog && (
        <HubActionDialog action="deactivate" onClose={() => setArchiveProjectDialog(false)} onConfirm={deleteProject} />
      )}

      {permanentDeleteDialog && (
        <HubActionDialog
          action="delete"
          onClose={() => setPermanentDeleteDialog(false)}
          onConfirm={permanentDeleteProject}
        />
      )}

      {restoreProjectDialog && (
        <HubActionDialog
          action="reactivate"
          onClose={() => setRestoreProjectDialog(false)}
          onConfirm={restoreProject}
        />
      )}
    </div>
  )
}

export default InformationBox
